import React, { useState } from "react";
import { Timestamp } from "firebase/firestore";
import { useBetContext } from "../../../../../context/ParlayBetContext.tsx";
import { isPrincipalBoardMarket, isSameSelectionSide, resolveSelectedTeams } from "../../../../../helpers/functions.tsx";
import { BetDetails, ParlaySelection } from "../../../../../models/MyBetModel.tsx";
import { Odd } from "../../../../../models/Odd_response_model.tsx";
import { Schedule } from "../../../../../models/Schedule.tsx";
import { DataGames } from "../../GamesProps/DataGames.tsx";
import { useUser } from "../../../../../context/UserContext.tsx";
import { SelectedOddSpecialParlay } from "./SelectedOddSpecialParlay.tsx";
import { useModal } from "../../../../../context/ModalContext.tsx";
import ParlayBets from "../../../modals/parlay/ParlayBets.tsx";
import Alert from "../../../../alerts/Alert.tsx";
import { fetchOddsSnapshot } from "../../../../../helpers/oddsSnapshotCache.tsx";
import { DEFAULT_PROPS_LOGO } from "../../../modals/BetLogo.tsx";
import { getFiguresTimeZone } from "../../../../../helpers/figuresTime.tsx";
import { applyRefreshedParlayOdds } from "../../../../../helpers/parlayOddsRefresh.ts";

interface ISpecialDataComponent {
    game: Schedule;
    showLimit: Record<string, number>;
    setShowLimit: React.Dispatch<React.SetStateAction<Record<string, number>>>;
    marketId: string;
    setShowLimitAlert: React.Dispatch<React.SetStateAction<boolean>>;
    setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
}

export function SpecialDataComponent(props: ISpecialDataComponent) {
    const { game, showLimit, marketId, setShowLimit, setShowLimitAlert, setErrorMessage } = props;
    const { bets, updateQuee, deleteBet, setBets } = useBetContext();
    const { user } = useUser();
    const { showModal } = useModal();
    const [showAlert, setShowAlert] = useState(false);
    const [loadingModal, setLoadingModal] = useState(false);

    if (!user?.userName) {
        throw new Error("User not authenticated");
    }

    const marketTitle = game.odds?.[0]?.market;
    const playerTimeZone = getFiguresTimeZone();
    const formattedDate = new Date(game.start_date).toLocaleDateString('en-US', {
        timeZone: playerTimeZone,
        weekday: 'long',
        month: 'long',
        day: 'numeric',
    }).toUpperCase();
    const gameTimeDate = new Date(game.start_date ?? '');
    const time = gameTimeDate.toLocaleTimeString('en-US', {
        timeZone: playerTimeZone,
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
    });

    const gameTime = Timestamp.fromDate(new Date(game.start_date));

    const _build = (selected: Odd): ParlaySelection => {
        const { teamId, opposingTeam } = resolveSelectedTeams(
            selected,
            game.home_competitors[0],
            game.away_competitors?.[0],
        );

        const logo = DEFAULT_PROPS_LOGO;
        const marketLower = String(selected.market ?? "").toLowerCase();
        let selectionType: string | undefined;
        if (marketLower.includes("moneyline")) {
            selectionType = "ML";
        } else if (selected.selection_line) {
            const line = selected.selection_line.toLowerCase();
            if (line === "over") selectionType = "O";
            else if (line === "under") selectionType = "U";
            else selectionType = selected.selection_line;
        }

        const gameType = isPrincipalBoardMarket(undefined, selected.market_id) ? undefined : "props";

        // Keep odds list usable in the modal (same market + same line / player)
        const relatedOdds = (game.odds ?? []).filter(o => {
            if (o.market_id !== selected.market_id) return false;
            if (selected.player_id || selected.team_id || selected.selection_line) {
                return isSameSelectionSide(selected, o);
            }
            return o.normalized_selection === selected.normalized_selection
                || o.selection === selected.selection
                || o.name === selected.name;
        });

        return {
            fixture_id: game.id,
            id_selection: selected.id,
            sport: game.sport.name,
            league: game.league.name,
            gameDate: gameTime,
            gameId: game.game_id,
            marketName: selected.market || selected.market_id,
            betName: selected.name ,
            betStatus: 'pending',
            odd: selected,
            odds: relatedOdds.length > 0 ? relatedOdds : [selected],
            teamId,
            opposingTeam,
            logo,
            ticket: '',
            ...(gameType ? { gameType } : {}),
            ...(selectionType ? { selectionType } : {}),
        } as ParlaySelection;
    };

    const onRequestBetsModal = (bet?: Partial<BetDetails>, selected: boolean = true) => {
        if (user.status !== "Active") {
            setErrorMessage("Your account is in read-only mode.");
            return;
        }

        const odd = bet?.odd;
        if (!odd) return;

        if (selected) {
            updateQuee(_build(odd));
        } else {
            deleteBet({ odd });
        }
    };

    // Same open flow as ParlayGameTeam → ParlayBets
    const onShowBetsModal = async () => {
        if (loadingModal) return;

        if (bets.length < 2) {
            setShowAlert(true);
            return;
        }

        setLoadingModal(true);

        try {
            const roundOddsPrice = (price: number) => Math.trunc(price / 5) * 5;

            const refreshedBets = await Promise.all(
                bets.map(async (bet) => {
                    if (!bet.fixture_id || !bet.odd?.market_id) return bet;

                    try {
                        const scheduleData = await fetchOddsSnapshot({
                            id: bet.fixture_id,
                            marketId: bet.odd.market_id,
                            team_id: bet.odd.team_id,
                            sportsbooks: [bet.odd.sportsbook],
                        });

                        const freshOdds = (scheduleData[0]?.odds ?? [])
                            .filter((o: Odd) => {
                                if (!bet.odd) return false;
                                if (bet.odd.player_id || bet.odd.team_id || bet.odd.selection_line) {
                                    return isSameSelectionSide(bet.odd, o);
                                }
                                return o.id === bet.odd.id || o.name === bet.betName;
                            })
                            .map((odd: Odd) => ({
                                ...odd,
                                price: roundOddsPrice(odd.price),
                            }));

                        if (freshOdds.length === 0) return bet;

                        return applyRefreshedParlayOdds(bet, freshOdds);
                    } catch (err) {
                        console.error('Error refreshing odds for special parlay bet:', err);
                        return bet;
                    }
                })
            );

            setBets(refreshedBets);
            showModal(
                <ParlayBets
                    key={refreshedBets.map(b => b.id_selection).join('-')}
                    setShowLimitAlert={setShowLimitAlert}
                    setErrorMessage={setErrorMessage}
                />
            );
        } catch {
            setLoadingModal(false);
            showModal(
                <ParlayBets
                    key={bets.map(b => b.id_selection).join('-')}
                    setShowLimitAlert={setShowLimitAlert}
                    setErrorMessage={setErrorMessage}
                />
            );
        } finally {
            setLoadingModal(false);
        }
    };

    return (
        <>
            {showAlert && (
                <Alert
                    message="At least 2 select are required for a parlay."
                    onClose={() => setShowAlert(false)}
                />
            )}
            <div>
                {bets.length > 0 && (
                    <div className='containerButtonParlay'>
                        <button
                            className='btn-custom'
                            disabled={loadingModal}
                            onClick={() => { void onShowBetsModal(); }}
                        >
                            {loadingModal ? 'Loading...' : `${bets.length} Pick(s)`}
                        </button>
                    </div>
                )}
                <div className="schedule-table">
                    <div className='container-table'>
                        <div className='d-flex justify-content-between align-items-center'>
                            {/* <div>
                                <img src={game.leagueLogo} className="league-logo" alt="" />
                            </div> */}
                            <div className="teamsMarkets-container ">
                                <span className='market-title teko'>
                                    {game.league.name} - {marketTitle}
                                </span>
                                <span className='teams-special teko'>
                                    {game.away_competitors[0]?.name} @ {game.home_competitors[0]?.name}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container-date-special">
                    <span className="date-special">{formattedDate}</span>  <span className="time-special teko">{time}</span>
                </div>
                <DataGames
                    marketData={[game]}
                    showLimit={showLimit}
                    setShowLimit={setShowLimit}
                    marketId={marketId}
                    onRequestBetsModal={onRequestBetsModal}
                    isSpecial={true}
                    bets={bets}
                    OddDisplay={SelectedOddSpecialParlay}
                />
            </div>
        </>
    );
}
